import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { OntologyLandscape, TierIcons, type LandscapeSpec, type OntologyModule, type TierRow } from "@/components/OntologyLandscape";

type PackRow = {
  id: string; name: string; semver: string; kind: string; tier: number | null;
  universal: boolean; industries: string[]; state: "delivered" | "draft" | "planned";
  installed: boolean; counts: { classes: number; properties: number; shapes: number } | null;
};
type CustomerLayer = {
  namespace_template: string;
  term_sets: Array<{ id: string; name: string; number: number; digest: string; count: number }>;
  summary: { terms: number; matched: number; extends: number; maps_to: number; new: number };
  count: number;
  terms: Array<{ label: string; aliases: string[]; language: string;
    state: "extends" | "maps_to" | "new"; reviewed: boolean;
    match: { pack: string; iri: string; relation: string } | null }>;
};

const TIERS: Array<Omit<TierRow, "modules">> = [
  { code: "0", label: "Foundation", sublabel: "Installed shared foundation", tone: "foundation", Icon: TierIcons.Layers },
  { code: "0.5", label: "Universal domains", sublabel: "Installed cross-industry modules", tone: "universal", Icon: TierIcons.ShieldCheck },
  { code: "1", label: "Standards", sublabel: "Only installed, rights-checked sources", tone: "standard", Icon: TierIcons.BookOpen },
  { code: "2", label: "Industry overlays", sublabel: "Installed industry modules", tone: "industry", Icon: TierIcons.Building2 },
  { code: "3", label: "Cross-domain applications", sublabel: "Installed T3 modules, if any", tone: "industry", Icon: TierIcons.Building2 },
  { code: "4", label: "Lines of business", sublabel: "Installed or platform-authored review drafts", tone: "lob", Icon: TierIcons.Package },
  { code: "5", label: "Regional overlays", sublabel: "Installed or platform-authored review drafts", tone: "regional", Icon: TierIcons.Globe2 },
  { code: "6", label: "Customer layer", sublabel: "Workspace-specific installed extensions", tone: "customer", Icon: TierIcons.Landmark },
];

function tierOf(row: PackRow): string {
  if (row.kind === "FOUNDATION") return "0";
  if (row.kind === "DOMAIN") return "0.5";
  if (row.kind === "STANDARD") return "1";
  if (row.kind === "LOB") return "4";
  if (row.kind === "REGIONAL") return "5";
  if (row.kind === "CUSTOMER") return "6";
  return row.tier === 3 ? "3" : "2";
}

function specFrom(rows: PackRow[], industry: string, customer?: CustomerLayer): LandscapeSpec {
  const visible = rows.filter((row) => industry === "all" || row.universal || row.industries.includes(industry));
  const delivered = visible.filter((row) => row.state === "delivered");
  const classes = delivered.reduce((sum, row) => sum + (row.counts?.classes ?? 0), 0);
  const properties = delivered.reduce((sum, row) => sum + (row.counts?.properties ?? 0), 0);
  const shapes = delivered.reduce((sum, row) => sum + (row.counts?.shapes ?? 0), 0);
  return {
    industry,
    reuseNote: "Delivered counts are read from this workspace's installed Turtle artifacts. Drafts are platform-authored and need SME review. Empty tiers have no installed artifact.",
    kpis: [
      { value: String(delivered.length), label: "Installed packs" },
      { value: String(classes), label: "OWL classes" },
      { value: String(properties), label: "OWL properties" },
      { value: String(shapes), label: "SHACL shapes" },
      { value: String(visible.filter((row) => row.state === "draft").length), label: "Review drafts" },
    ],
    tiers: TIERS.map((tier) => ({ ...tier, modules: [
      ...visible.filter((row) => tierOf(row) === tier.code).map((row): OntologyModule => ({
      slug: row.name, name: row.name, version: row.semver,
      entities: (row.counts?.classes ?? 0) + (row.counts?.properties ?? 0),
      shapes: row.counts?.shapes, countsAvailable: row.counts !== null,
      examples: [], state: row.state,
      })),
      ...(tier.code === "6" ? (customer?.term_sets ?? []).map((set): OntologyModule => ({
        slug: set.id, name: set.name, version: String(set.number), entities: set.count,
        unit: "approved terms", countsAvailable: true, examples: [], state: "delivered",
      })) : []),
    ] })),
  };
}

export function WorkspaceLandscape({ workspace, termSetVersionIds }: {
  workspace: string; termSetVersionIds: string[];
}) {
  const [industry, setIndustry] = useState("all");
  const [offset, setOffset] = useState(0);
  const query = useQuery<{ packs: PackRow[] }>({
    queryKey: ["workspace-landscape", workspace],
    queryFn: () => rawFetch(`/packs/installs/landscape/?workspace=${encodeURIComponent(workspace)}`),
    enabled: Boolean(workspace),
  });
  const customer = useQuery<CustomerLayer>({
    queryKey: ["composer-customer-layer", workspace, termSetVersionIds, offset],
    queryFn: () => rawFetch("/ontology/term-sets/customer-layer/", {
      method: "POST", body: JSON.stringify({ workspace,
        term_set_version_ids: termSetVersionIds, limit: 50, offset }),
    }),
    enabled: Boolean(workspace && termSetVersionIds.length),
  });
  if (query.isLoading) return <p className="text-xs text-muted" role="status">Loading installed pack landscape…</p>;
  if (query.isError) return <p className="text-xs text-bad" role="alert">Could not load installed packs. Retry from this workspace.</p>;
  const rows = query.data?.packs ?? [];
  const industries = [...new Set(rows.flatMap((row) => row.industries))].sort();
  return <div className="space-y-3"><OntologyLandscape spec={specFrom(rows, industry, customer.data)} header={<label className="text-xs text-muted">
    Industry <select className="input ml-2 w-auto" value={industry} onChange={(event) => setIndustry(event.target.value)}>
      <option value="all">All installed</option>
      {industries.map((item) => <option value={item} key={item}>{item}</option>)}
    </select>
  </label>} />
    {termSetVersionIds.length > 0 && <section className="border border-border bg-panel/40 p-3" aria-label="T6 customer layer">
      <h3 className="text-sm font-semibold">T6 · Pinned customer terms</h3>
      {customer.isLoading && <p className="text-xs text-muted" role="status">Matching approved terms to installed packs…</p>}
      {customer.isError && <p className="text-xs text-bad" role="alert">Could not preview the pinned term sets.</p>}
      {customer.data && <>
        <label className="mt-2 block text-xs text-muted">Canonical namespace for generated class IRIs
          <input className="input mt-1 w-full font-mono" value={customer.data.namespace_template} readOnly
            aria-label="Canonical customer namespace" />
          <span className="mt-1 block">Version is filled in when a draft is created. This canonical namespace is read-only.</span>
        </label>
        <p className="mt-2 text-xs font-medium" role="status">
          {customer.data.summary.terms} terms, {customer.data.summary.matched} matched, {customer.data.summary.new} new
          ({customer.data.summary.extends} extends, {customer.data.summary.maps_to} maps to).
        </p>
        <div className="mt-2 max-h-56 overflow-auto border border-border">
          <table className="w-full text-left text-xs"><thead><tr><th className="p-2">Term</th><th className="p-2">Status</th><th className="p-2">Installed concept</th></tr></thead>
            <tbody>{customer.data.terms.map((term, index) => <tr key={`${offset}-${index}`} className="border-t border-border">
              <td className="p-2">{term.label}{term.language && <span className="ml-1 text-muted">({term.language})</span>}</td>
              <td className="p-2">{term.state === "maps_to" ? "Maps to" : term.state === "extends" ? "Extends" : "New"}
                {!term.reviewed && term.match && <span className="ml-1 text-muted">· suggested</span>}</td>
              <td className="p-2 font-mono text-[10px]">{term.match ? `${term.match.pack} · ${term.match.iri}` : "—"}</td>
            </tr>)}</tbody></table>
        </div>
        {customer.data.count > 50 && <div className="mt-2 flex items-center gap-2 text-xs">
          <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>
          <span>{offset + 1}–{Math.min(offset + 50, customer.data.count)} of {customer.data.count}</span>
          <button className="btn" disabled={offset + 50 >= customer.data.count} onClick={() => setOffset(offset + 50)}>Next</button>
        </div>}
      </>}
    </section>}
  </div>;
}
